/**
 * BackDismiss — the phone's back button closes an open sheet instead of
 * leaving the page (Funktionstest F19).
 *
 * A sheet is not a route: it sits on top of the screen that opened it, and
 * the URL does not change. So the browser's back button had nothing of its
 * own to undo and went to the previous page — or, in an installed PWA on a
 * fresh start, out of the app.
 *
 * Opening a sheet therefore pushes one history entry with the SAME URL (no
 * hash change, so the hash router does not react — it listens to
 * `hashchange` only). Back pops that entry, `popstate` fires, and the sheet
 * closes. Closed any other way (×, scrim, Escape), the sheet gives its entry
 * back with `history.back()`, so the next back press goes where it used to.
 *
 * One guard at a time: the sheets are modal and never stack.
 */

let current = null;
// history.back() calls of our own whose popstate is still on its way — they
// must not close a sheet that was opened in the meantime.
let ownBacks = 0;

window.addEventListener( "popstate", () =>
{
    if ( ownBacks > 0 )
    {
        ownBacks--;
        return;
    }
    if ( !current )
    {
        return;
    }
    const guard = current;
    current = null;
    guard.onBack();
} );

/**
 * Let the back button close something.
 *
 * @param {Function} onBack  Called when the guest presses back. It must close
 *                           without calling the returned release.
 * @returns {Function} release — call it when closed another way.
 */
export function guardBack( onBack )
{
    const token = String( Date.now() ) + Math.random();
    history.pushState( { "belturoSheet" : token }, "", location.href );
    current = { onBack, token };

    return function release()
    {
        if ( !current || current.token !== token )
        {
            return;
        }
        current = null;
        // Decided in the next task: a link tapped inside the sheet closes it
        // first and navigates right after, in the same task. By then the new
        // page is the entry on top, and going back would undo the navigation;
        // only when our own entry is still on top is it given back.
        setTimeout( () =>
        {
            if ( history.state && history.state.belturoSheet === token )
            {
                ownBacks++;
                history.back();
            }
        }, 0 );
    };
}
